import * as React from 'react';
import { useIsMobile, useMobileLine } from '../../../hooks/use-mobile';
import { cn } from '../../../utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../drawer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../sheet';

/**
 * Which family the root mounted, so each part renders the matching piece.
 */
type ResponsiveDialogFamily = 'dialog' | 'drawer' | 'sheet';

const ResponsiveDialogContext = React.createContext<ResponsiveDialogFamily>('dialog');

function useResponsiveDialog(): ResponsiveDialogFamily {
  return React.useContext(ResponsiveDialogContext);
}

/**
 * Whether the content around a part is the `xl` size, which pads the drawer's
 * body instead of its popup, so the drawer's own header padding would double
 * it.
 */
const ResponsiveDialogSizeContext = React.createContext<'default' | 'xl'>('default');

const HEADERS = { dialog: DialogHeader, drawer: DrawerHeader, sheet: SheetHeader } as const;

const FOOTERS = { dialog: DialogFooter, drawer: DrawerFooter, sheet: SheetFooter } as const;

/**
 * A centred Dialog above the app's mobile line and, below it, the sheet the
 * app's `MobileLineProvider` asks for: the kit's Drawer, or the bottom Sheet
 * when there is no provider. One set of children for all three.
 */
function ResponsiveDialog(props: React.ComponentProps<typeof Dialog>) {
  const isMobile = useIsMobile();
  const { sheet } = useMobileLine();
  const family = isMobile ? sheet : 'dialog';
  useUnlockBodyOnClose(props.open ?? false);

  const { onOpenChange } = props;
  // Base UI adds its event details; the consumer's contract is Radix's `(open) => void`.
  const handleDrawerOpenChange = React.useCallback(
    (open: boolean) => onOpenChange?.(open),
    [onOpenChange],
  );

  const renderRoot = () => {
    if (family === 'drawer')
      return (
        <Drawer
          defaultOpen={props.defaultOpen}
          modal={props.modal}
          open={props.open}
          onOpenChange={handleDrawerOpenChange}
        >
          {props.children}
        </Drawer>
      );
    if (family === 'sheet') return <Sheet {...props} />;
    return <Dialog {...props} />;
  };

  return <ResponsiveDialogContext value={family}>{renderRoot()}</ResponsiveDialogContext>;
}

/**
 * Radix's scroll-lock can leave `pointer-events: none` on `<body>` after a
 * mid-transition close, freezing the page.
 */
function useUnlockBodyOnClose(open: boolean): void {
  React.useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => {
      if (document.body.style.pointerEvents !== 'none') return;
      const openLayer = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"]',
      );
      if (!openLayer) document.body.style.pointerEvents = '';
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);
}

/**
 * A body-portaled combobox list renders outside the dialog, so its clicks must
 * not dismiss it.
 */
function allowComboboxInteraction(event: {
  target: EventTarget | null;
  preventDefault: () => void;
}) {
  const target = event.target;
  if (target instanceof Element && target.closest('[data-combobox-list]')) event.preventDefault();
}

interface ResponsiveDialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogContent
> {
  /**
   * `xl` is wider and capped in height, and a body marked `flex-1` scrolls
   * inside it (`ScrollArea`) while the header and footer stay reachable.
   * `default` keeps the dialog's own size.
   */
  size?: 'default' | 'xl';
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  ResponsiveDialogContentProps
>(({ children, className, overlayClassName, overlayStyle, size = 'default', ...props }, ref) => {
  const family = useResponsiveDialog();
  const isXl = size === 'xl';
  const closeShape = isXl ? 'round' : 'plain';

  /**
   * A consumer sizes the centred dialog; below the line its width and height
   * classes would pin the sheet, so the sheet's own size is merged in last.
   */
  const classes = {
    drawer: cn(
      { 'bg-surface-1 flex flex-col overflow-hidden': isXl },
      className,
      'max-h-(--drawer-content-max-height,none) w-full max-w-none sm:max-w-none',
    ),
    // The padding sits on the scrolling body, not the popup, so a child's negative margin
    // bleeds into it instead of being clipped by the body's overflow.
    drawerBody: cn({ 'gap-4 p-7': isXl }),
    sheet: cn(
      'max-h-[90vh] overflow-y-auto rounded-t-xl',
      {
        'bg-surface-1 flex max-h-[calc(100dvh-1.5rem)] flex-col overflow-hidden p-7': isXl,
      },
      className,
      'w-full max-w-none sm:max-w-none',
    ),
  };

  // the sheets draw their own backdrops, so the dialog's backdrop props stay with the dialog
  if (family === 'drawer')
    return (
      <DrawerContent
        ref={ref}
        className={classes.drawer}
        closeShape={closeShape}
        contentClassName={classes.drawerBody}
        isSheet
        {...props}
      >
        <ResponsiveDialogSizeContext value={size}>{children}</ResponsiveDialogSizeContext>
      </DrawerContent>
    );
  if (family === 'sheet')
    return (
      <SheetContent
        ref={ref}
        closeShape={closeShape}
        side="bottom"
        className={classes.sheet}
        onInteractOutside={allowComboboxInteraction}
        onPointerDownOutside={allowComboboxInteraction}
        {...props}
      >
        {children}
      </SheetContent>
    );
  return (
    <DialogContent
      ref={ref}
      closeShape={closeShape}
      className={cn(
        {
          'bg-surface-1 flex max-h-[min(calc(100dvh-2rem),55.3125rem)] max-w-190 flex-col overflow-hidden p-7 sm:rounded-4xl':
            isXl,
        },
        className,
      )}
      overlayClassName={overlayClassName}
      overlayStyle={overlayStyle}
      onInteractOutside={allowComboboxInteraction}
      onPointerDownOutside={allowComboboxInteraction}
      {...props}
    >
      {children}
    </DialogContent>
  );
});
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent';

function ResponsiveDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const family = useResponsiveDialog();
  const size = React.useContext(ResponsiveDialogSizeContext);
  const Header = HEADERS[family];
  const isPaddedBody = family === 'drawer' && size === 'xl';
  const classes = { header: cn({ 'gap-0 p-0': isPaddedBody }, className) };
  return (
    <Header
      className={classes.header}
      {...props}
    />
  );
}

function ResponsiveDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const family = useResponsiveDialog();
  const size = React.useContext(ResponsiveDialogSizeContext);
  const Footer = FOOTERS[family];
  const isPaddedBody = family === 'drawer' && size === 'xl';
  const classes = { footer: cn({ 'p-0': isPaddedBody }, className) };
  return (
    <Footer
      className={classes.footer}
      {...props}
    />
  );
}

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, face, ...props }, ref) => {
  const family = useResponsiveDialog();

  const classes = {
    drawerTitle: cn(
      {
        'font-display mb-title-display last:mb-0': face === 'display' || face === 'display-caps',
        'text-xl/none uppercase': face === 'display-caps',
      },
      className,
    ),
  };

  if (family === 'drawer')
    return (
      <DrawerTitle
        ref={ref}
        className={classes.drawerTitle}
        {...props}
      />
    );
  const Title = family === 'sheet' ? SheetTitle : DialogTitle;
  return (
    <Title
      ref={ref}
      className={className}
      face={face}
      {...props}
    />
  );
});
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle';

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>((props, ref) => {
  const family = useResponsiveDialog();
  if (family === 'drawer')
    return (
      <DrawerDescription
        ref={ref}
        {...props}
      />
    );
  const Description = family === 'sheet' ? SheetDescription : DialogDescription;
  return (
    <Description
      ref={ref}
      {...props}
    />
  );
});
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription';

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
};
